import { Controller } from "@hotwired/stimulus"
import { isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js'

export default class extends Controller {
  static targets = ["form", "submitButton", "errorContainer", "field", "submitting", "formContent"]
  static values = {
    templateId: Number,
    staffId: Number,
    requiresLocation: Boolean,
    maxFileSize: Number,
    allowedTypes: Array,
    mode: String,
    submissionId: Number
  }

  connect() {
    // Initialize validation state
    this.validationState = {}
    this.locationData = null

    // Add event listeners to all field targets
    this.fieldTargets.forEach(field => {
      field.addEventListener('blur', this.validateField.bind(this))
      field.addEventListener('change', this.validateField.bind(this))
    })

    // Set up MutationObserver for style changes
    this.setupStyleObserver()

    this.formTarget.addEventListener("submit", this.handleSubmit.bind(this))
  }

  disconnect() {
    // Clean up field event listeners
    this.fieldTargets.forEach(field => {
      field.removeEventListener('blur', this.validateField.bind(this))
      field.removeEventListener('change', this.validateField.bind(this))
    })

    // Disconnect the MutationObserver if it exists
    if (this.styleObserver) {
      this.styleObserver.disconnect()
    }
  }

  setupStyleObserver() {
    // Configuration for the observer
    const observerConfig = {
      attributes: true,
      attributeFilter: ['style'],
      subtree: true
    }

    // Create and start the observer
    this.styleObserver = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          // Debounce to avoid multiple rapid fires
          clearTimeout(this.resizeTimeout)
          this.resizeTimeout = setTimeout(() => {
            window.dispatchEvent(new Event('resize'))
          }, 100)
        }
      })
    })

    // Start observing the body element
    this.styleObserver.observe(document.body, observerConfig)
  }

  // Submit the form with location data if required
  submitForm(event) {
    event.preventDefault()

    // Validate the form first
    if (!this.validateAllFields()) {
      return
    }

    // Show submitting state
    this.toggleSubmitting(true)

    // If location is required, get it before submitting
    if (this.requiresLocationValue) {
      this.getLocationAndSubmit()
    } else {
      this.submitFormData()
    }
  }

  // Get location data and then submit the form
  getLocationAndSubmit() {
    if (!navigator.geolocation) {
      this.showError("Geolocation is not supported by your browser")
      this.toggleSubmitting(false)
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        // Store location data
        this.locationData = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        }

        // Submit the form with location data
        this.submitFormData()
      },
      (error) => {
        this.toggleSubmitting(false)
        this.handleLocationError(error)
      },
      { enableHighAccuracy: true, timeout: 10000 }
    )
  }

  // Handle location errors
  handleLocationError(error) {
    let message = ""
    switch (error.code) {
      case error.PERMISSION_DENIED:
        message = "Location access was denied. Please enable location services to submit this form."
        break
      case error.POSITION_UNAVAILABLE:
        message = "Location information is unavailable."
        break
      case error.TIMEOUT:
        message = "The request to get location timed out."
        break
      default:
        message = "An unknown error occurred while getting location."
    }
    this.showError(message)
  }

  // Submit the form data to the server
  submitFormData() {
    // Create FormData object
    const formData = new FormData(this.formTarget)

    // Add location data if required
    if (this.requiresLocationValue && this.locationData) {
      formData.append('form_submission[latitude]', this.locationData.latitude)
      formData.append('form_submission[longitude]', this.locationData.longitude)
    }

    // Add template ID and staff ID
    formData.append('form_template_id', this.templateIdValue)
    formData.append('staff_id', this.staffIdValue)

    // Add submit flag
    formData.append('submit', 'true')

    // Determine the endpoint and method based on mode
    const endpoint = this.modeValue === 'edit' 
      ? `/worker-app/submissions/${this.submissionIdValue}`
      : '/worker-app/submissions'
    const method = this.modeValue === 'edit' ? 'PATCH' : 'POST'

    // Send the form data
    fetch(endpoint, {
      method: method,
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: formData
    })
    .then(response => {
      if (!response.ok) {
        return response.json().then(data => {
          throw new Error(data.message || 'Form submission failed')
        })
      }
      return response.json()
    })
    .then(data => {
      // Show success message and close modal
      this.showSuccess(data.message || (this.modeValue === 'edit' ? 'Form updated successfully' : 'Form submitted successfully'))

      // Close the modal after a delay
      setTimeout(() => {
        // Find the close button and click it
        const closeButton = this.element.querySelector('[data-hs-overlay-close]') ||
                           this.element.querySelector('[aria-label="Close"]')
        if (closeButton) {
          closeButton.click()
        }

        // Reload the page to show updated status
        window.location.reload()
      }, 1500)
    })
    .catch(error => {
      this.toggleSubmitting(false)
      this.showError(error.message || 'An error occurred while submitting the form')
    })
  }

  // Validate a single field
  validateField(event) {
    const field = event.target
    const fieldId = field.dataset.fieldId
    const fieldType = field.dataset.fieldType
    const required = field.dataset.required === 'true'

    // Get value based on field type
    let value
    if (fieldType === 'multiselect') {
      // For multiselect, get all selected values
      const selectedOptions = Array.from(field.selectedOptions || [])
      value = selectedOptions.map(option => option.value)
    } else if (fieldType === 'checkbox') {
      // For checkboxes, get all checked values for this field ID
      const checkboxes = this.formTarget.querySelectorAll(`input[type="checkbox"][data-field-id="${fieldId}"]`)
      value = Array.from(checkboxes).filter(cb => cb.checked).map(cb => cb.value)
    } else if (fieldType === 'radio') {
      // For radio buttons, get the selected value
      const selectedRadio = this.formTarget.querySelector(`input[type="radio"][data-field-id="${fieldId}"]:checked`)
      value = selectedRadio ? selectedRadio.value : ''
    } else {
      value = field.value
    }

    

    // Skip validation for empty optional fields
    if ((!value || (Array.isArray(value) && value.length === 0)) && !required) {
      this.clearFieldError(fieldId)
      this.validationState[fieldId] = true
      return
    }

    // Perform client-side validation
    let isValid = true
    let errorMessage = ''

    if (required && (!value || (Array.isArray(value) && value.length === 0))) {
      isValid = false
      errorMessage = 'This field is required'
    } else if (value) {
      switch (fieldType) {
        case 'email':
          isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
          errorMessage = 'Please enter a valid email address'
          break
        case 'number':
          isValid = !isNaN(value) && value !== ''
          errorMessage = 'Please enter a valid number'
          break
        case 'phone':
          try {
            const defaultCountry = field.dataset.defaultCountry || 'GH'
            const phoneNumber = parsePhoneNumberFromString(value, defaultCountry)
            isValid = phoneNumber ? phoneNumber.isValid() : false

            // If valid, update the field value to international format
            if (isValid) {
              field.value = phoneNumber.format('E.164')
            }

            errorMessage = 'Please enter a valid phone number'
          } catch (e) {
            isValid = false
            errorMessage = 'Please enter a valid phone number'
          }
          break
        case 'date':
          isValid = /^\d{4}-\d{2}-\d{2}$/.test(value)
          errorMessage = 'Please enter a valid date (YYYY-MM-DD)'
          break
        case 'multiselect':
        case 'checkbox':
          isValid = Array.isArray(value) && value.length > 0
          errorMessage = 'Please select at least one option'
          break
        case 'radio':
          isValid = value !== ''
          errorMessage = 'Please select an option'
          break
      }
    }

    if (isValid) {
      this.clearFieldError(fieldId)
      this.validationState[fieldId] = true
    } else {
      this.showFieldError(fieldId, errorMessage)
      this.validationState[fieldId] = false
    }

    // Validate all fields to ensure proper submit button state
    this.validateAllFields()
  }

  // Validate all fields at once
  validateAllFields() {
    let isValid = true

    this.fieldTargets.forEach(field => {
      const fieldId = field.dataset.fieldId
      const fieldType = field.dataset.fieldType
      const required = field.dataset.required === 'true'

      // Get value based on field type
      let value
      if (fieldType === 'multiselect') {
        const selectedOptions = Array.from(field.selectedOptions || [])
        value = selectedOptions.map(option => option.value)
      } else if (fieldType === 'checkbox') {
        // For checkboxes, get all checked values for this field ID
        const checkboxes = this.formTarget.querySelectorAll(`input[type="checkbox"][data-field-id="${fieldId}"]`)
        value = Array.from(checkboxes).filter(cb => cb.checked).map(cb => cb.value)
      } else if (fieldType === 'radio') {
        // For radio buttons, get the selected value
        const selectedRadio = this.formTarget.querySelector(`input[type="radio"][data-field-id="${fieldId}"]:checked`)
        value = selectedRadio ? selectedRadio.value : ''
      } else {
        value = field.value
      }

      // Skip validation for empty optional fields
      if (!value && !required) {
        this.clearFieldError(fieldId)
        this.validationState[fieldId] = true
        return
      }

      // Validate required fields
      if (required && (!value || (Array.isArray(value) && value.length === 0))) {
        this.showFieldError(fieldId, 'This field is required')
        this.validationState[fieldId] = false
        isValid = false
        return
      }

      // Validate field type if value exists
      if (value) {
        let fieldValid = true
        switch (fieldType) {
          case 'email':
            fieldValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
            break
          case 'number':
            fieldValid = !isNaN(value) && value !== ''
            break
          case 'phone':
            try {
              const defaultCountry = field.dataset.defaultCountry || 'GH'
              const phoneNumber = parsePhoneNumberFromString(value, defaultCountry)
              fieldValid = phoneNumber ? phoneNumber.isValid() : false

              // If valid, update the field value to international format
              if (fieldValid) {
                field.value = phoneNumber.format('E.164')
              }
            } catch (e) {
              fieldValid = false
            }
            break
          case 'date':
            fieldValid = /^\d{4}-\d{2}-\d{2}$/.test(value)
            break
          case 'multiselect':
          case 'checkbox':
            fieldValid = Array.isArray(value) && value.length > 0
            break
          case 'radio':
            fieldValid = value !== ''
            break
        }

        if (!fieldValid) {
          this.showFieldError(fieldId, `Please enter a valid ${fieldType}`)
          this.validationState[fieldId] = false
          isValid = false
        } else {
          this.clearFieldError(fieldId)
          this.validationState[fieldId] = true
        }
      }
    })

    return isValid
  }

  // Show error message for a field
  showFieldError(fieldId, message) {
    const field = this.fieldTargets.find(f => f.dataset.fieldId === fieldId)
    if (!field) return

    // Add error class to the field
    field.classList.add('border-red-500')

    // Find or create error message container
    let errorElement = document.getElementById(`error-${fieldId}`)
    if (!errorElement) {
      errorElement = document.createElement('div')
      errorElement.id = `error-${fieldId}`
      errorElement.classList.add('text-red-500', 'text-xs', 'mt-1')
      field.parentNode.appendChild(errorElement)
    }

    errorElement.textContent = message
    errorElement.classList.remove('hidden')
  }

  // Clear error message for a field
  clearFieldError(fieldId) {
    const field = this.fieldTargets.find(f => f.dataset.fieldId === fieldId)
    if (!field) return

    // Remove error class from the field
    field.classList.remove('border-red-500')

    // Hide error message
    const errorElement = document.getElementById(`error-${fieldId}`)
    if (errorElement) {
      errorElement.classList.add('hidden')
    }
  }

  // Toggle submitting state
  toggleSubmitting(isSubmitting) {
    if (this.hasSubmittingTarget && this.hasFormContentTarget) {
      if (isSubmitting) {
        this.submittingTarget.classList.remove('hidden')
        this.formContentTarget.classList.add('hidden')
      } else {
        this.submittingTarget.classList.add('hidden')
        this.formContentTarget.classList.remove('hidden')
      }
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = isSubmitting
    }
  }

  // Show general error message
  showError(message) {
    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.textContent = message
      this.errorContainerTarget.classList.remove('hidden')
    } else {
      alert(message)
    }
  }

  // Show success message
  showSuccess(message) {
    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.textContent = message
      this.errorContainerTarget.classList.remove('hidden', 'bg-red-100', 'text-red-800')
      this.errorContainerTarget.classList.add('bg-green-100', 'text-green-800')
    }
  }

  handleSubmit(event) {
    event.preventDefault()

    // Validate all fields before submission
    const isValid = this.validateAllFields()
    if (!isValid) {
      return
    }

    // Submit the form if all validations pass
    this.formTarget.submit()
  }

  validateFileUpload(field) {
    const file = field.files[0]
    if (!file) return true

    // Check file size
    if (this.maxFileSizeValue && file.size > this.maxFileSizeValue) {
      this.showError(`File size must be less than ${this.formatFileSize(this.maxFileSizeValue)}`)
      return false
    }

    // Check file type
    if (this.allowedTypesValue && this.allowedTypesValue.length > 0) {
      const fileType = file.type
      if (!this.allowedTypesValue.includes(fileType)) {
        this.showError(`File type must be one of: ${this.allowedTypesValue.join(", ")}`)
        return false
      }
    }

    return true
  }

  validateSignature(field) {
    // For signature fields, we expect a base64 image
    if (!field.value.startsWith("data:image")) {
      this.showError("Invalid signature format")
      return false
    }

    // Check signature size if maxFileSize is set
    if (this.maxFileSizeValue) {
      const base64Data = field.value.split(",")[1]
      const decodedSize = (base64Data.length * 3) / 4
      if (decodedSize > this.maxFileSizeValue) {
        this.showError(`Signature size must be less than ${this.formatFileSize(this.maxFileSizeValue)}`)
        return false
      }
    }

    return true
  }

  // Handle file uploads and previews
  handleFileUpload(event) {
    const input = event.target
    const fieldType = input.dataset.fieldType
    const previewContainer = input.closest('label').querySelector('.preview-container')
    const allowMultiple = input.dataset.allowMultiple === 'true'
    const maxFiles = parseInt(input.dataset.maxFiles) || null

    if (!input.files || !input.files.length) return

    // Validate file count for multiple uploads
    if (allowMultiple && maxFiles && input.files.length > maxFiles) {
      this.showError(`Maximum ${maxFiles} file${maxFiles > 1 ? 's' : ''} allowed`)
      input.value = '' // Clear the input
      return
    }

    // For single file uploads, only take the first file
    const files = allowMultiple ? Array.from(input.files) : [input.files[0]]

    // Clear existing previews
    if (previewContainer) {
      previewContainer.innerHTML = ''
      previewContainer.classList.remove('hidden')
    }

    // Handle each file
    files.forEach(file => {
      if (fieldType === 'image' && previewContainer) {
        this.previewImage(file, previewContainer)
      } else if (fieldType === 'file' && previewContainer) {
        this.previewFile(file, previewContainer)
      }
    })
  }

  // Preview an image file
  previewImage(file, container) {
    if (!file.type.startsWith('image/')) return

    const reader = new FileReader()
    const div = document.createElement('div')
    div.className = 'relative'

    reader.onload = (e) => {
      div.innerHTML = `
        <img src="${e.target.result}" class="w-full rounded-lg border shadow-sm" alt="Preview">
        <button type="button" 
                class="absolute top-2 right-2 text-sm font-medium text-white bg-black bg-opacity-50 px-2 py-1 rounded hover:bg-opacity-75"
                data-action="click->form-submission#removePreview">
          Remove
        </button>
      `
    }

    reader.readAsDataURL(file)
    container.appendChild(div)
  }

  // Preview a file (non-image)
  previewFile(file, container) {
    const div = document.createElement('div')
    div.className = 'relative bg-white rounded-lg border shadow-sm p-4'

    div.innerHTML = `
      <div class="flex items-center space-x-2">
        <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-gray-900 truncate" title="${file.name}">
            ${file.name}
          </p>
          <p class="text-xs text-gray-500">
            ${this.formatFileSize(file.size)}
          </p>
        </div>
        <button type="button"
                class="text-sm text-red-600 hover:text-red-800"
                data-action="click->form-submission#removePreview">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    `

    container.appendChild(div)
  }

  // Remove a preview image
  removePreview(event) {
    const previewDiv = event.target.closest('.relative')
    if (previewDiv) {
      previewDiv.remove()

      // If no previews left, hide the container
      const container = previewDiv.closest('.preview-container')
      if (container && !container.children.length) {
        container.classList.add('hidden')
      }
    }
  }

  // Remove an uploaded image
  async removeImage(event) {
    const button = event.target
    const imageId = button.dataset.imageId
    const fieldId = button.closest('[data-field-id]').dataset.fieldId

    if (!imageId || !fieldId) return

    try {
      const response = await fetch(`/forms/submissions/remove_image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          image_id: imageId,
          field_id: fieldId
        })
      })

      if (response.ok) {
        // Remove the image element from the DOM
        const imageContainer = button.closest('.relative')
        if (imageContainer) {
          imageContainer.remove()

          // If no images left, show the "No images uploaded" message
          const container = imageContainer.closest('.grid')
          if (container && !container.children.length) {
            container.innerHTML = '<span class="text-gray-500">No images uploaded</span>'
          }
        }
      } else {
        const data = await response.json()
        this.showError(data.message || 'Failed to remove image')
      }
    } catch (error) {
      console.error('Error removing image:', error)
      this.showError('Failed to remove image')
    }
  }

  formatFileSize(bytes) {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }
}
